// Codes for vital-sign values Orbit calculates where LOINC has no exact
// term. LOINC links were checked on the production terminology server.
import type { Coding } from '@medplum/fhirtypes'
import { loincCoding } from '#src/models/loinc.ts'
import {
  type TemplateQuestion,
  templateQuestionConcept,
  templateQuestionLoincMap,
} from '#src/terminology/define.ts'
import { ASSESSMENT_QUESTIONS_SYSTEM } from '#src/terminology/types.ts'

const ORTHOSTATIC_LOINC_COMMENT =
  '34538-9 Orthostatic blood pressure is one pressure difference, which Orbit records as the systolic change'

const VITAL_QUESTIONS = [
  {
    code: 'orthostatic-diastolic-change',
    display: 'Orthostatic diastolic change',
    definition:
      'Standing diastolic blood pressure less lying (or, without lying, sitting) diastolic; negative is a drop',
    domain: 'vital-signs',
    capture: { kind: 'quantity', unit: 'mm[Hg]' },
    loinc: {
      code: '34538-9',
      equivalence: 'wider',
      comment: ORTHOSTATIC_LOINC_COMMENT,
    },
  },
] as const satisfies readonly TemplateQuestion[]

type VitalCode = (typeof VITAL_QUESTIONS)[number]['code']

export function vitalCoding(code: VitalCode): Coding {
  const question = VITAL_QUESTIONS.find((candidate) => candidate.code === code)
  return {
    system: ASSESSMENT_QUESTIONS_SYSTEM,
    code,
    display: question?.display ?? code,
  }
}

export const VITAL_QUESTION_CONCEPTS = VITAL_QUESTIONS.map(
  templateQuestionConcept
)

export const VITAL_QUESTION_LOINC_MAP = VITAL_QUESTIONS.map(
  templateQuestionLoincMap
)

// Orthostatic vital signs: blood pressure and heart rate lying, sitting and
// standing. The panel (34553-8) is resolved from NLM like any LOINC panel.
export const ORTHOSTATIC_READINGS = {
  lying: {
    label: 'Lying',
    systolic: loincCoding('8461-6', 'Systolic blood pressure--supine'),
    diastolic: loincCoding('8455-8', 'Diastolic blood pressure--supine'),
    heartRate: loincCoding('68999-2', 'Heart rate --supine'),
  },
  sitting: {
    label: 'Sitting',
    systolic: loincCoding('8459-0', 'Systolic blood pressure--sitting'),
    diastolic: loincCoding('8453-3', 'Diastolic blood pressure--sitting'),
    heartRate: loincCoding('69000-8', 'Heart rate --sitting'),
  },
  standing: {
    label: 'Standing',
    systolic: loincCoding('8460-8', 'Systolic blood pressure--standing'),
    diastolic: loincCoding('8454-1', 'Diastolic blood pressure--standing'),
    heartRate: loincCoding('69001-6', 'Heart rate --standing'),
  },
} as const

// LOINC's one orthostatic pressure difference, in the NLM panel, holds the
// systolic change: the consensus definition leads with it.
export const ORTHOSTATIC_SYSTOLIC_CHANGE_CODING = loincCoding(
  '34538-9',
  'Orthostatic blood pressure'
)
export const ORTHOSTATIC_DIASTOLIC_CHANGE_CODING = vitalCoding(
  'orthostatic-diastolic-change'
)
export const ORTHOSTATIC_HEART_RATE_CHANGE_CODING = loincCoding(
  '69408-3',
  'Orthostatic heart rate [Difference in rate]'
)

export const ORTHOSTATIC_CONSENSUS_SOURCE = {
  citation:
    'Freeman R, Wieling W, Axelrod FB, et al. Consensus statement on the definition of orthostatic hypotension, neurally mediated syncope and the postural tachycardia syndrome. Clin Auton Res 2011;21(2):69–72.',
  url: 'https://doi.org/10.1007/s10286-011-0119-5',
} as const
